import { FunctionComponent, useState, useEffect } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import styles from './HeightInput.module.css';

interface HeightInputProps {
  label?: string;
  value?: { feet?: number; inches?: number; cm?: number };
  onChange: (value: { feet?: number; inches?: number; cm?: number }) => void;
  unit?: 'imperial' | 'metric';
  required?: boolean;
  helperText?: string;
  error?: string;
  hasNextButton?: boolean;
  onNext?: () => void;
}

const HeightInput: FunctionComponent<HeightInputProps> = ({
  label = 'What is your height?',
  value = {},
  onChange,
  unit = 'imperial',
  required = false,
  helperText,
  error,
  hasNextButton = false,
  onNext
}) => {
  const [feet, setFeet] = useState<string>(value.feet?.toString() || '');
  const [inches, setInches] = useState<string>(value.inches?.toString() || '');
  const [cm, setCm] = useState<string>(value.cm?.toString() || '');
  const [unitSystem, setUnitSystem] = useState<'imperial' | 'metric'>(unit);

  useEffect(() => {
    // Convert between units when switching
    if (unitSystem === 'metric' && feet && inches) {
      const totalInches = (parseInt(feet) || 0) * 12 + (parseInt(inches) || 0);
      const centimeters = Math.round(totalInches * 2.54);
      setCm(centimeters.toString());
    } else if (unitSystem === 'imperial' && cm) {
      const totalInches = Math.round((parseInt(cm) || 0) / 2.54);
      const feetValue = Math.floor(totalInches / 12);
      const inchesValue = totalInches % 12;
      setFeet(feetValue.toString());
      setInches(inchesValue.toString());
    }
  }, [unitSystem]);

  const handleFeetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setFeet(value);
      updateValue({ feet: value ? parseInt(value) : undefined, inches: inches ? parseInt(inches) : undefined });
    }
  };

  const handleInchesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || (/^\d+$/.test(value) && parseInt(value) < 12)) {
      setInches(value);
      updateValue({ feet: feet ? parseInt(feet) : undefined, inches: value ? parseInt(value) : undefined });
    }
  };

  const handleCmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setCm(value);
      updateValue({ cm: value ? parseInt(value) : undefined });
    }
  };

  const updateValue = (newValue: { feet?: number; inches?: number; cm?: number }) => {
    onChange(newValue);
  };

  const isValid = () => {
    if (unitSystem === 'imperial') {
      return feet !== '' && inches !== '';
    } else {
      return cm !== '';
    }
  };

  const getHeightDisplay = () => {
    if (unitSystem === 'imperial' && feet && inches) {
      return `${feet}'${inches}"`;
    } else if (unitSystem === 'metric' && cm) {
      return `${cm} cm`;
    }
    return '';
  };

  return (
    <div className={styles.container}>
      {label && (
        <label className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div className={styles.unitToggle}>
        <button
          type="button"
          className={`${styles.unitButton} ${unitSystem === 'imperial' ? styles.active : ''}`}
          onClick={() => setUnitSystem('imperial')}
        >
          Feet & Inches
        </button>
        <button
          type="button"
          className={`${styles.unitButton} ${unitSystem === 'metric' ? styles.active : ''}`}
          onClick={() => setUnitSystem('metric')}
        >
          Centimeters
        </button>
      </div>

      {unitSystem === 'imperial' ? (
        <div className={styles.imperialInputs}>
          <div className={styles.inputGroup}>
            <input
              type="number"
              className={`${styles.input} ${error ? styles.error : ''}`}
              value={feet}
              onChange={handleFeetChange}
              placeholder="5"
              min="0"
              max="9"
              required={required}
            />
            <span className={styles.unit}>ft</span>
          </div>
          
          <div className={styles.inputGroup}>
            <input
              type="number"
              className={`${styles.input} ${error ? styles.error : ''}`}
              value={inches}
              onChange={handleInchesChange}
              placeholder="8"
              min="0"
              max="11"
              required={required}
            />
            <span className={styles.unit}>in</span>
          </div>
        </div>
      ) : (
        <div className={styles.metricInput}>
          <div className={styles.inputGroup}>
            <input
              type="number"
              className={`${styles.input} ${error ? styles.error : ''}`}
              value={cm}
              onChange={handleCmChange}
              placeholder="170"
              min="0"
              max="300"
              required={required}
            />
            <span className={styles.unit}>cm</span>
          </div>
        </div>
      )}

      {isValid() && (
        <div className={styles.heightDisplay}>
          Your height: <strong>{getHeightDisplay()}</strong>
        </div>
      )}

      {helperText && !error && (
        <div className={styles.helperText}>
          <span>{helperText}</span>
        </div>
      )}

      {error && (
        <div className={styles.errorMessage}>
          <span>{error}</span>
        </div>
      )}

      {hasNextButton && (
        <button 
          type="button" 
          className={styles.nextButton} 
          onClick={onNext}
          disabled={required && !isValid()}
        >
          Next
          <ChevronRightIcon />
        </button>
      )}
    </div>
  );
};

export default HeightInput;